import React from "react";
import { Moon, Sun, Monitor, Palette, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useTheme } from "@/components/ThemeProvider";

export function ThemeToggle(): JSX.Element {
  const { theme, setTheme, actualTheme } = useTheme();

  const themes = [
    {
      name: "Light",
      value: "light" as const,
      icon: Sun,
      description: "Clean, bright interface",
    },
    {
      name: "Dark",
      value: "dark" as const,
      icon: Moon,
      description: "Easy on the eyes",
    },
    {
      name: "System",
      value: "system" as const,
      icon: Monitor,
      description: "Matches your device",
    },
  ];

  const getCurrentIcon = () => {
    if (theme === "system") {
      return actualTheme === "dark" ? Moon : Sun;
    }
    return theme === "dark" ? Moon : Sun;
  };

  const CurrentIcon = getCurrentIcon();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="relative h-9 w-9 p-0 hover:bg-muted/60 transition-all duration-300 group">
          <CurrentIcon className="h-4 w-4 transition-all duration-300 group-hover:scale-110" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56 p-2 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-gray-200/60 dark:border-gray-700/40 shadow-2xl shadow-gray-500/20 dark:shadow-black/40 rounded-xl"
      >
        <div className="flex items-center gap-2 px-3 py-2 mb-2 text-sm font-medium text-muted-foreground">
          <Palette className="h-4 w-4" />
          Theme Preference
        </div>
        {themes.map((themeOption) => {
          const Icon = themeOption.icon;
          const isSelected = theme === themeOption.value;

          return (
            <DropdownMenuItem
              key={themeOption.value}
              onClick={() => setTheme(themeOption.value)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-200 group"
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100/80 dark:bg-gray-800/80 group-hover:bg-gray-200/80 dark:group-hover:bg-gray-700/80 transition-colors">
                <Icon className="h-4 w-4 text-gray-700 dark:text-gray-200" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{themeOption.name}</span>
                  {isSelected && <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{themeOption.description}</p>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
